import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { Col, Row, Spin, Tag, Tooltip, message, Drawer, Empty } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { ButtonItem, TableItem, SelectItem, InputNumberItem } from "../../../Components/Items";
import KpiCard from "./KpiCard";
import BotonesReporte from "./BotonesReporte";
import {
    formatMs,
    colorMs,
    formatMb,
    colorPct,
    tagMetodo,
    truncarPath,
    colorServidor,
    etiquetaDisparador,
    colorDisparador,
} from "./utilsPerformance";

moment.locale("es");

const TARJETA = {
    backgroundColor: "white",
    padding: 15,
    borderRadius: 5,
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
};

// Más marcadores que esto y la gráfica se vuelve una reja ilegible.
const MAX_MARCADORES = 30;

const SinDatos = ({ alto }) => (
    <div
        style={{
            height: alto,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
        }}
    >
        Sin datos en el rango seleccionado
    </div>
);

class Recursos extends Component {
    state = {
        cargado: false,
        loadingResumen: false,
        loadingSerie: false,
        loadingSaturaciones: false,
        loadingTareas: false,
        loadingDetalle: false,

        servidores: [],
        servidoresSel: [],
        // Las gráficas de varias métricas a la vez sólo tienen sentido de un servidor:
        // tres series de CPU más tres de memoria en el mismo eje no se leen.
        servidorFoco: null,
        bucketSegundos: null,

        resumen: {},
        porServidor: [],
        puntos: [],
        saturaciones: [],
        tareas: [],

        minEnVuelo: null,
        minLagMs: null,

        drawerVisible: false,
        snapshot: null,
        requestsDetalle: [],
    };

    componentDidMount() {
        if (this.props.activo) this.consultarTodo();
    }

    componentDidUpdate(prevProps) {
        const cambioRango = prevProps.rango !== this.props.rango;
        const pidieronRefrescar = prevProps.refrescarNonce !== this.props.refrescarNonce;
        const seActivo = !prevProps.activo && this.props.activo;

        // Consultar sólo cuando la pestaña está a la vista: cambiar el rango desde la
        // otra pestaña no debe disparar seis peticiones que nadie va a ver.
        if (this.props.activo && (cambioRango || pidieronRefrescar)) {
            this.consultarTodo();
            return;
        }
        if (seActivo && (!this.state.cargado || cambioRango || pidieronRefrescar)) {
            this.consultarTodo();
        }
    }

    rangoParams() {
        const [desde, hasta] = this.props.rango || [];
        return {
            desde: desde ? desde.toISOString() : undefined,
            hasta: hasta ? hasta.toISOString() : undefined,
        };
    }

    filtrosServidor() {
        const { servidoresSel } = this.state;
        return servidoresSel.length ? { servidores: servidoresSel } : {};
    }

    pedir = (ruta, cuerpo) =>
        axios.post(ruta, { ...this.rangoParams(), ...cuerpo }, { headers: this.props.headersToken });

    consultarTodo = () => {
        this.setState({ cargado: true });
        this.consultarServidores();
        this.consultarResumen();
        this.consultarSerie();
        this.consultarSaturaciones();
        this.consultarTareas();
    };

    consultarServidores = () => {
        this.pedir("performance/recursos/servidores", {})
            .then((res) => {
                if (res.data.success !== 2) return;
                const servidores = res.data.servidores || [];
                this.setState((prev) => ({
                    servidores,
                    // El de más muestras es el que más probablemente esté atendiendo.
                    servidorFoco:
                        prev.servidorFoco && servidores.some((s) => s.servidor === prev.servidorFoco)
                            ? prev.servidorFoco
                            : servidores[0]?.servidor || null,
                }));
            })
            .catch(() => {});
    };

    consultarResumen = () => {
        this.setState({ loadingResumen: true });
        this.pedir("performance/recursos/resumen", this.filtrosServidor())
            .then((res) => {
                this.setState({ loadingResumen: false });
                if (res.data.success === 2) {
                    this.setState({
                        resumen: res.data.resumen || {},
                        porServidor: res.data.porServidor || [],
                    });
                } else {
                    message.error(res.data.message || "Error al consultar el resumen de recursos");
                }
            })
            .catch(() => {
                this.setState({ loadingResumen: false });
                message.error("Error al consultar el resumen de recursos");
            });
    };

    consultarSerie = () => {
        this.setState({ loadingSerie: true });
        this.pedir("performance/recursos/serie", this.filtrosServidor())
            .then((res) => {
                this.setState({ loadingSerie: false });
                if (res.data.success === 2) {
                    this.setState({
                        puntos: res.data.puntos || [],
                        bucketSegundos: res.data.bucketSegundos,
                    });
                } else {
                    message.error(res.data.message || "Error al consultar la serie de recursos");
                }
            })
            .catch(() => {
                this.setState({ loadingSerie: false });
                message.error("Error al consultar la serie de recursos");
            });
    };

    consultarSaturaciones = () => {
        this.setState({ loadingSaturaciones: true });
        this.pedir("performance/recursos/saturaciones", {
            ...this.filtrosServidor(),
            minEnVuelo: this.state.minEnVuelo || undefined,
            minLagMs: this.state.minLagMs || undefined,
        })
            .then((res) => {
                this.setState({ loadingSaturaciones: false });
                if (res.data.success === 2) {
                    this.setState({ saturaciones: res.data.data || [] });
                } else {
                    message.error(res.data.message || "Error al consultar los momentos de saturación");
                }
            })
            .catch(() => {
                this.setState({ loadingSaturaciones: false });
                message.error("Error al consultar los momentos de saturación");
            });
    };

    consultarTareas = () => {
        this.setState({ loadingTareas: true });
        this.pedir("performance/recursos/tareas", this.filtrosServidor())
            .then((res) => {
                this.setState({ loadingTareas: false });
                if (res.data.success === 2) this.setState({ tareas: res.data.data || [] });
            })
            .catch(() => this.setState({ loadingTareas: false }));
    };

    abrirDrawer = (record) => {
        this.setState({ drawerVisible: true, snapshot: null, requestsDetalle: [], loadingDetalle: true });
        axios
            .post(
                "performance/recursos/saturacionDetalle",
                { snapshotId: record._id },
                { headers: this.props.headersToken }
            )
            .then((res) => {
                this.setState({ loadingDetalle: false });
                if (res.data.success === 2) {
                    this.setState({
                        snapshot: res.data.snapshot,
                        requestsDetalle: res.data.requests || [],
                    });
                } else {
                    message.error(res.data.message || "Error al consultar el detalle");
                }
            })
            .catch(() => {
                this.setState({ loadingDetalle: false });
                message.error("Error al consultar el detalle");
            });
    };

    cerrarDrawer = () => this.setState({ drawerVisible: false, snapshot: null, requestsDetalle: [] });

    // ── Construcción de series ────────────────────────────────────────────────

    puntosVisibles() {
        const { puntos, servidoresSel } = this.state;
        if (!servidoresSel.length) return puntos;
        return puntos.filter((p) => servidoresSel.includes(p.servidor));
    }

    /** Una serie por servidor de una sola métrica. */
    seriePorServidor(campo) {
        const porServidor = new Map();
        for (const p of this.puntosVisibles()) {
            if (!porServidor.has(p.servidor)) porServidor.set(p.servidor, []);
            porServidor.get(p.servidor).push({ x: new Date(p.t), y: p[campo] ?? 0 });
        }
        return Array.from(porServidor.entries()).map(([servidor, data]) => ({
            id: servidor,
            color: colorServidor(servidor),
            data,
        }));
    }

    /** Varias métricas de un solo servidor. */
    serieDeUnServidor(campos) {
        const { servidorFoco } = this.state;
        const puntos = this.puntosVisibles().filter((p) => !servidorFoco || p.servidor === servidorFoco);
        return campos
            .map(({ campo, id, color }) => ({
                id,
                color,
                data: puntos
                    .filter((p) => p[campo] !== null && p[campo] !== undefined)
                    .map((p) => ({ x: new Date(p.t), y: p[campo] })),
            }))
            .filter((s) => s.data.length);
    }

    marcadoresSaturacion() {
        return this.state.saturaciones.slice(0, MAX_MARCADORES).map((s) => ({
            axis: "x",
            value: new Date(s.tomadaEn),
            lineStyle: { stroke: "#cf1322", strokeWidth: 1.5, strokeDasharray: "4 4" },
        }));
    }

    ejesComunes(conEjeInferior) {
        return {
            xScale: { type: "time", format: "native", precision: "minute" },
            xFormat: "time:%d/%m %H:%M",
            axisTop: null,
            axisRight: null,
            axisBottom: conEjeInferior
                ? { format: "%d/%m %H:%M", tickRotation: 45, tickSize: 5, tickPadding: 8 }
                : null,
            enableGridX: false,
            useMesh: true,
            enableSlices: "x",
            // Con cientos de puntos, los puntos tapan la línea.
            pointSize: 0,
            lineWidth: 2,
            curve: "monotoneX",
        };
    }

    // ── Render ────────────────────────────────────────────────────────────────

    renderControles() {
        const { servidores, servidoresSel, servidorFoco } = this.state;
        return (
            <div style={{ ...TARJETA, marginBottom: 10 }}>
                <Row gutter={[10, 10]} align="middle">
                    <Col>
                        <span style={{ fontSize: 12, color: "#666" }}>Servidores:</span>
                    </Col>
                    <Col>
                        <SelectItem
                            noBuscar
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="Todos"
                            width={320}
                            value={servidoresSel}
                            onChange={(v) => this.setState({ servidoresSel: v || [] }, this.consultarTodo)}
                            options={servidores.map((s) => ({
                                value: s.servidor,
                                label: `${s.servidor} (${s.disparos} saturaciones)`,
                            }))}
                        />
                    </Col>
                    <Col>
                        <span style={{ fontSize: 12, color: "#666" }}>Detalle de:</span>
                    </Col>
                    <Col>
                        <SelectItem
                            noBuscar
                            width={220}
                            placeholder="Servidor"
                            value={servidorFoco}
                            onChange={(v) => this.setState({ servidorFoco: v })}
                            options={servidores.map((s) => ({ value: s.servidor, label: s.servidor }))}
                        />
                    </Col>
                    <Col flex="auto" style={{ textAlign: "right" }}>
                        <span style={{ fontSize: 11, color: "#999" }}>
                            {this.state.bucketSegundos
                                ? `Agregado cada ${
                                      this.state.bucketSegundos >= 60
                                          ? Math.round(this.state.bucketSegundos / 60) + " min"
                                          : this.state.bucketSegundos + " s"
                                  }`
                                : null}
                        </span>
                    </Col>
                </Row>
            </div>
        );
    }

    renderKpis() {
        const r = this.state.resumen || {};
        const peor = r.peorRequest;
        const col = { xs: 12, sm: 12, md: 8, lg: Math.floor(24 / 5), xl: Math.floor(24 / 5) };
        return (
            <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
                <Col {...col}>
                    <KpiCard
                        titulo="Momentos de saturación"
                        valor={(r.eventosSaturacion || 0).toLocaleString()}
                        color={r.eventosSaturacion ? "#cf1322" : "#52c41a"}
                    />
                </Col>
                <Col {...col}>
                    <KpiCard
                        titulo="Event loop bloqueado (máx)"
                        valor={formatMs(r.lagMaxMs || 0)}
                        color={colorMs(r.lagMaxMs || 0)}
                    />
                </Col>
                <Col {...col}>
                    <KpiCard
                        titulo="Pico de requests en vuelo"
                        valor={(r.enVueloMax || 0).toLocaleString()}
                        color={r.enVueloMax >= 40 ? "#fa8c16" : "#00BFD6"}
                    />
                </Col>
                <Col {...col}>
                    <KpiCard
                        titulo="Request más acaparador"
                        valor={formatMs(r.masViejoMsMax || 0)}
                        color={colorMs(r.masViejoMsMax || 0)}
                        detalle={peor ? `${peor.method || ""} ${truncarPath(peor.path)}` : null}
                    />
                </Col>
                <Col {...col}>
                    <KpiCard
                        titulo="RSS máx / CPU máx"
                        valor={formatMb(r.rssMbMax || 0)}
                        color={colorPct(r.cpuPctMax || 0)}
                        detalle={`CPU ${Math.round(r.cpuPctMax || 0)}% · pool encolado máx ${
                            r.poolEncoladosMax || 0
                        }`}
                    />
                </Col>
            </Row>
        );
    }

    renderPresion() {
        if (!this.props.activo) return null;
        const seriesEnVuelo = this.seriePorServidor("enVueloMax");
        const seriesLag = this.seriePorServidor("lagMaxMs");
        const marcadores = this.marcadoresSaturacion();
        const hayDatos = seriesEnVuelo.some((s) => s.data.length);

        return (
            <div style={{ ...TARJETA, marginBottom: 10 }}>
                <h3 style={{ margin: 0, marginBottom: 4 }}>Presión sobre el proceso</h3>
                <p style={{ margin: 0, marginBottom: 10, fontSize: 12, color: "#888" }}>
                    Arriba, cuántos requests estaban corriendo a la vez. Abajo, cuánto tiempo estuvo el
                    proceso sin poder atender nada. Las líneas rojas punteadas son los momentos de
                    saturación que quedaron registrados.
                </p>
                {!hayDatos ? (
                    <SinDatos alto={300} />
                ) : (
                    <>
                        <div style={{ height: 220 }}>
                            <ResponsiveLine
                                data={seriesEnVuelo}
                                margin={{ top: 10, right: 30, bottom: 10, left: 60 }}
                                yScale={{ type: "linear", min: 0, max: "auto" }}
                                colors={{ datum: "color" }}
                                markers={marcadores}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 8,
                                    legend: "requests en vuelo",
                                    legendOffset: -45,
                                    legendPosition: "middle",
                                }}
                                enableArea
                                areaOpacity={0.12}
                                {...this.ejesComunes(false)}
                            />
                        </div>
                        <div style={{ height: 230 }}>
                            <ResponsiveLine
                                data={seriesLag}
                                margin={{ top: 10, right: 30, bottom: 70, left: 60 }}
                                // symlog y no lineal: el lag pasa de 5 ms a 8 s y en escala
                                // lineal todo lo que no sea el pico se aplasta contra el eje.
                                yScale={{ type: "symlog", min: 0, max: "auto" }}
                                colors={{ datum: "color" }}
                                markers={marcadores}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 8,
                                    legend: "bloqueo (ms)",
                                    legendOffset: -45,
                                    legendPosition: "middle",
                                }}
                                {...this.ejesComunes(true)}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }

    renderCpuYPool() {
        if (!this.props.activo) return null;
        const seriesCpu = this.serieDeUnServidor([
            { campo: "cpuPctAvg", id: "CPU del proceso (%)", color: "#00BFD6" },
            { campo: "load1Max", id: "Carga del sistema (load1)", color: "#722ed1" },
        ]);
        const seriesPool = this.serieDeUnServidor([
            { campo: "poolEnUsoMax", id: "Conexiones en uso", color: "#00BFD6" },
            { campo: "poolEncoladosMax", id: "Operaciones esperando", color: "#cf1322" },
        ]);

        const poolMax = this.puntosVisibles()
            .filter((p) => !this.state.servidorFoco || p.servidor === this.state.servidorFoco)
            .reduce((m, p) => Math.max(m, p.poolMax || 0), 0);

        return (
            <Row gutter={10} style={{ marginBottom: 10 }}>
                <Col xs={24} lg={14}>
                    <div style={{ ...TARJETA, height: 400 }}>
                        <h3 style={{ margin: 0, marginBottom: 10 }}>
                            CPU y carga — {this.state.servidorFoco || "sin servidor"}
                        </h3>
                        {!seriesCpu.length ? (
                            <SinDatos alto={320} />
                        ) : (
                            <div style={{ height: 320 }}>
                                <ResponsiveLine
                                    data={seriesCpu}
                                    margin={{ top: 10, right: 30, bottom: 70, left: 55 }}
                                    yScale={{ type: "linear", min: 0, max: "auto" }}
                                    colors={{ datum: "color" }}
                                    axisLeft={{ tickSize: 5, tickPadding: 8 }}
                                    legends={[
                                        {
                                            anchor: "top-left",
                                            direction: "row",
                                            translateY: -10,
                                            itemWidth: 190,
                                            itemHeight: 14,
                                            symbolSize: 10,
                                            symbolShape: "circle",
                                        },
                                    ]}
                                    {...this.ejesComunes(true)}
                                />
                            </div>
                        )}
                    </div>
                </Col>
                <Col xs={24} lg={10}>
                    <div style={{ ...TARJETA, height: 400 }}>
                        <h3 style={{ margin: 0, marginBottom: 2 }}>Pool de Mongo</h3>
                        <p style={{ margin: 0, marginBottom: 8, fontSize: 11, color: "#888" }}>
                            Si "esperando" sube de cero, la lentitud es espera por un socket, no CPU:
                            el event loop puede estar ocioso y todo tardar igual.
                        </p>
                        {!seriesPool.length ? (
                            <SinDatos alto={300} />
                        ) : (
                            <div style={{ height: 300 }}>
                                <ResponsiveLine
                                    data={seriesPool}
                                    margin={{ top: 10, right: 20, bottom: 70, left: 45 }}
                                    yScale={{ type: "linear", min: 0, max: "auto" }}
                                    colors={{ datum: "color" }}
                                    axisLeft={{ tickSize: 5, tickPadding: 8 }}
                                    markers={
                                        poolMax
                                            ? [
                                                  {
                                                      axis: "y",
                                                      value: poolMax,
                                                      legend: `techo del pool (${poolMax})`,
                                                      legendOrientation: "horizontal",
                                                      lineStyle: {
                                                          stroke: "#cf1322",
                                                          strokeWidth: 1.5,
                                                          strokeDasharray: "4 4",
                                                      },
                                                  },
                                              ]
                                            : []
                                    }
                                    {...this.ejesComunes(true)}
                                />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        );
    }

    renderMemoria() {
        if (!this.props.activo) return null;
        const series = this.serieDeUnServidor([
            { campo: "rssMbMax", id: "RSS", color: "#cf1322" },
            { campo: "heapUsadoMbAvg", id: "Heap usado", color: "#00BFD6" },
            { campo: "heapTotalMbAvg", id: "Heap total", color: "#722ed1" },
            { campo: "externalMbAvg", id: "External", color: "#fa8c16" },
        ]);

        return (
            <div style={{ ...TARJETA, marginBottom: 10, height: 380 }}>
                <h3 style={{ margin: 0, marginBottom: 10 }}>
                    Memoria del proceso — {this.state.servidorFoco || "sin servidor"}
                </h3>
                {!series.length ? (
                    <SinDatos alto={300} />
                ) : (
                    <div style={{ height: 300 }}>
                        <ResponsiveLine
                            data={series}
                            margin={{ top: 10, right: 30, bottom: 70, left: 60 }}
                            yScale={{ type: "linear", min: 0, max: "auto" }}
                            colors={{ datum: "color" }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 8,
                                legend: "MB",
                                legendOffset: -48,
                                legendPosition: "middle",
                            }}
                            legends={[
                                {
                                    anchor: "top-left",
                                    direction: "row",
                                    translateY: -10,
                                    itemWidth: 110,
                                    itemHeight: 14,
                                    symbolSize: 10,
                                    symbolShape: "circle",
                                },
                            ]}
                            {...this.ejesComunes(true)}
                        />
                    </div>
                )}
            </div>
        );
    }

    renderSaturaciones() {
        const columnas = [
            {
                title: "Fecha",
                dataIndex: "tomadaEn",
                key: "tomadaEn",
                width: 160,
                render: (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm:ss") : "-"),
            },
            {
                title: "Servidor",
                dataIndex: "servidor",
                key: "servidor",
                width: 150,
                render: (v) => <Tag color={colorServidor(v)}>{v}</Tag>,
            },
            {
                title: "Disparador",
                dataIndex: "disparadores",
                key: "disparadores",
                width: 190,
                render: (v = []) => (
                    <>
                        {v.map((d) => (
                            <Tag key={d} color={colorDisparador(d)} style={{ marginBottom: 2 }}>
                                {etiquetaDisparador(d)}
                            </Tag>
                        ))}
                    </>
                ),
            },
            {
                title: "En vuelo",
                dataIndex: "enVuelo",
                key: "enVuelo",
                width: 95,
                align: "right",
                sorter: (a, b) => a.enVuelo - b.enVuelo,
                render: (v, r) => (
                    <span style={{ fontWeight: "bold", color: v >= 40 ? "#cf1322" : "#333" }}>
                        {v}
                        {r.zombis ? <span style={{ color: "#fa8c16" }}> (+{r.zombis}z)</span> : null}
                    </span>
                ),
            },
            {
                title: "Lleva",
                dataIndex: "masViejoMs",
                key: "masViejoMs",
                width: 105,
                align: "right",
                sorter: (a, b) => a.masViejoMs - b.masViejoMs,
                defaultSortOrder: "descend",
                render: (v) => <span style={{ color: colorMs(v), fontWeight: "bold" }}>{formatMs(v)}</span>,
            },
            {
                title: "Sospechoso",
                dataIndex: "sospechoso",
                key: "sospechoso",
                render: (v) =>
                    v ? (
                        <Tooltip title={v.path}>
                            <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                                {v.method} {truncarPath(v.path)}
                            </span>
                        </Tooltip>
                    ) : (
                        <span style={{ color: "#999" }}>ningún request</span>
                    ),
            },
            {
                title: "Bloqueo",
                dataIndex: "lagMaxMs",
                key: "lagMaxMs",
                width: 100,
                align: "right",
                render: (v) => <span style={{ color: colorMs(v) }}>{formatMs(v)}</span>,
            },
            {
                title: "Pool",
                dataIndex: "poolEnUso",
                key: "poolEnUso",
                width: 110,
                align: "right",
                render: (v, r) =>
                    r.poolMax ? (
                        <span style={{ color: r.poolEncolados > 0 ? "#cf1322" : "#333" }}>
                            {v}/{r.poolMax}
                            {r.poolEncolados > 0 ? ` +${r.poolEncolados}` : ""}
                        </span>
                    ) : (
                        "—"
                    ),
            },
            {
                title: "CPU / RSS",
                dataIndex: "cpuPct",
                key: "cpuPct",
                width: 130,
                align: "right",
                render: (v, r) => (
                    <span style={{ color: colorPct(v) }}>
                        {Math.round(v)}% · {formatMb(r.rssMb)}
                    </span>
                ),
            },
            {
                title: "Tareas",
                dataIndex: "trabajos",
                key: "trabajos",
                width: 90,
                align: "center",
                render: (v = []) =>
                    v.length ? (
                        <Tooltip title={v.join(", ")}>
                            <Tag color="volcano">{v.length}</Tag>
                        </Tooltip>
                    ) : (
                        <span style={{ color: "#ccc" }}>0</span>
                    ),
            },
        ];

        return (
            <div style={{ ...TARJETA, marginBottom: 10 }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: 10 }}>
                    <Col>
                        <h3 style={{ margin: 0 }}>Momentos de saturación</h3>
                        <span style={{ fontSize: 12, color: "#888" }}>
                            Clic en un renglón para ver exactamente qué estaba corriendo en ese instante.
                        </span>
                    </Col>
                    <Col>
                        <Row gutter={10} align="middle">
                            <Col>
                                <span style={{ fontSize: 12, color: "#666" }}>Mín. en vuelo:</span>
                            </Col>
                            <Col>
                                <InputNumberItem
                                    min={0}
                                    value={this.state.minEnVuelo}
                                    onChange={(v) => this.setState({ minEnVuelo: v })}
                                    style={{ width: 90 }}
                                    placeholder="0"
                                />
                            </Col>
                            <Col>
                                <span style={{ fontSize: 12, color: "#666" }}>Mín. bloqueo (ms):</span>
                            </Col>
                            <Col>
                                <InputNumberItem
                                    min={0}
                                    step={500}
                                    value={this.state.minLagMs}
                                    onChange={(v) => this.setState({ minLagMs: v })}
                                    style={{ width: 110 }}
                                    placeholder="0"
                                />
                            </Col>
                            <Col>
                                <ButtonItem
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={this.consultarSaturaciones}
                                >
                                    Aplicar
                                </ButtonItem>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <TableItem
                    loading={this.state.loadingSaturaciones}
                    dataSource={this.state.saturaciones}
                    columns={columnas}
                    size="small"
                    rowKey={(r) => r._id}
                    defaultPageSize={20}
                    onRow={(record) => ({
                        onClick: () => this.abrirDrawer(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </div>
        );
    }

    renderTareas() {
        if (!this.props.activo) return null;
        const { tareas } = this.state;
        const data = tareas
            .slice(0, 12)
            .map((t) => ({
                tarea: t.nombre,
                "En saturación": t.ticksEnSaturacion,
                Normal: Math.max(0, t.ticks - t.ticksEnSaturacion),
            }))
            .reverse();

        return (
            <div style={{ ...TARJETA, marginBottom: 10, height: 340 }}>
                <h3 style={{ margin: 0, marginBottom: 2 }}>Trabajo de background en el rango</h3>
                <p style={{ margin: 0, marginBottom: 8, fontSize: 12, color: "#888" }}>
                    Crons y procesos masivos. Nada de esto pasa por una ruta HTTP, así que no aparece en
                    la pestaña de Requests: es la respuesta cuando ningún endpoint explica la saturación.
                </p>
                {!data.length ? (
                    <SinDatos alto={240} />
                ) : (
                    <div style={{ height: 240 }}>
                        <ResponsiveBar
                            data={data}
                            keys={["En saturación", "Normal"]}
                            indexBy="tarea"
                            layout="horizontal"
                            margin={{ top: 10, right: 30, bottom: 45, left: 220 }}
                            padding={0.25}
                            colors={["#cf1322", "#d9d9d9"]}
                            borderRadius={3}
                            axisLeft={{ tickSize: 5, tickPadding: 6 }}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 8,
                                legend: "muestras en que estuvo corriendo",
                                legendPosition: "middle",
                                legendOffset: 35,
                            }}
                            enableGridY={false}
                            labelSkipWidth={20}
                        />
                    </div>
                )}
            </div>
        );
    }

    renderDrawer() {
        const { snapshot, requestsDetalle, loadingDetalle } = this.state;

        const columnas = [
            {
                title: "#",
                dataIndex: "indice",
                key: "indice",
                width: 50,
                align: "right",
                render: (v, r, i) => i + 1,
            },
            {
                title: "Lleva",
                dataIndex: "edadMs",
                key: "edadMs",
                width: 110,
                align: "right",
                render: (v, r) => (
                    <span style={{ color: colorMs(v), fontWeight: "bold" }}>
                        {formatMs(v)}
                        {r.zombi ? (
                            <Tooltip title="El cliente ya abortó, pero el handler sigue trabajando">
                                <Tag color="volcano" style={{ marginLeft: 4 }}>
                                    z
                                </Tag>
                            </Tooltip>
                        ) : null}
                    </span>
                ),
            },
            {
                title: "Método",
                dataIndex: "method",
                key: "method",
                width: 85,
                render: (v) => tagMetodo(v),
            },
            {
                title: "Endpoint",
                dataIndex: "originalUrl",
                key: "originalUrl",
                render: (v, r) => (
                    <Tooltip title={v || r.path}>
                        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v || r.path}</span>
                    </Tooltip>
                ),
            },
            {
                title: "Empresa",
                dataIndex: "empresaNombre",
                key: "empresaNombre",
                width: 200,
                render: (v, r) => <Tooltip title={String(r.empresaId || "")}>{v || "—"}</Tooltip>,
            },
            {
                title: "Usuario",
                dataIndex: "usuarioNombre",
                key: "usuarioNombre",
                width: 180,
                render: (v, r) => <Tooltip title={String(r.usuarioId || "")}>{v || "—"}</Tooltip>,
            },
            { title: "IP", dataIndex: "ip", key: "ip", width: 130 },
            {
                title: "Parámetros",
                dataIndex: "parametros",
                key: "parametros",
                width: 260,
                render: (v) =>
                    v ? (
                        <Tooltip title={v}>
                            <span style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{v}</span>
                        </Tooltip>
                    ) : (
                        <span style={{ color: "#ccc" }}>—</span>
                    ),
            },
        ];

        const kpi = { xs: 12, sm: 8, md: 6, lg: 4 };

        return (
            <Drawer
                title={
                    snapshot ? (
                        /* antd 4.15.6 no tiene el prop `extra` del Drawer (llegó en 4.17): los
                           botones del reporte van dentro del título. */
                        <Row align="middle" justify="space-between" gutter={10} style={{ paddingRight: 30 }}>
                            <Col>
                                Saturación en <strong>{snapshot.servidor}</strong> —{" "}
                                {moment(snapshot.tomadaEn).format("DD/MM/YYYY HH:mm:ss")}
                            </Col>
                            <Col>
                                <BotonesReporte
                                    endpoint="performance/recursos/reporte"
                                    cuerpo={{ snapshotId: snapshot._id }}
                                    headersToken={this.props.headersToken}
                                />
                            </Col>
                        </Row>
                    ) : (
                        "Detalle de la saturación"
                    )
                }
                placement="right"
                width={Math.min(window.innerWidth - 50, 1250)}
                /* antd 4.15.6: el prop es `visible`. Con `open` el Drawer no abre nunca,
                   porque el componente hace {...rest} y luego sobreescribe open: visible. */
                visible={this.state.drawerVisible}
                onClose={this.cerrarDrawer}
                destroyOnClose
            >
                <Spin spinning={loadingDetalle}>
                    {!snapshot ? (
                        <Empty description="Sin detalle" />
                    ) : (
                        <>
                            <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="En vuelo"
                                        valor={snapshot.requests?.enVuelo ?? 0}
                                        color="#cf1322"
                                        detalle={
                                            snapshot.requests?.zombis
                                                ? `${snapshot.requests.zombis} zombis`
                                                : null
                                        }
                                    />
                                </Col>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="Bloqueo máx"
                                        valor={formatMs(snapshot.eventLoop?.bloqueoMaxMs || 0)}
                                        color={colorMs(snapshot.eventLoop?.bloqueoMaxMs || 0)}
                                        detalle={`${Math.round(
                                            snapshot.eventLoop?.pctBloqueado || 0
                                        )}% de la muestra`}
                                    />
                                </Col>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="CPU"
                                        valor={`${Math.round(snapshot.cpu?.pctPorNucleo || 0)}%`}
                                        color={colorPct(snapshot.cpu?.pctPorNucleo || 0)}
                                        detalle={`${snapshot.cpu?.nucleos || "?"} núcleos`}
                                    />
                                </Col>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="RSS / heap"
                                        valor={formatMb(snapshot.memoria?.rssMb || 0)}
                                        detalle={`heap ${formatMb(snapshot.memoria?.heapUsadoMb || 0)}`}
                                    />
                                </Col>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="Libre en el sistema"
                                        valor={formatMb(snapshot.so?.memLibreMb || 0)}
                                        detalle={`load1 ${(snapshot.so?.load1 || 0).toFixed(2)}`}
                                    />
                                </Col>
                                <Col {...kpi}>
                                    <KpiCard
                                        titulo="Pool de Mongo"
                                        valor={
                                            snapshot.pool?.disponible
                                                ? `${snapshot.pool.enUso}/${snapshot.pool.max}`
                                                : "—"
                                        }
                                        color={snapshot.pool?.encolados > 0 ? "#cf1322" : "#52c41a"}
                                        detalle={
                                            snapshot.pool?.disponible
                                                ? `${snapshot.pool.encolados} esperando`
                                                : "no disponible"
                                        }
                                    />
                                </Col>
                            </Row>

                            <div style={{ marginBottom: 12 }}>
                                {(snapshot.disparadores || []).map((d) => (
                                    <Tag key={d} color={colorDisparador(d)}>
                                        {etiquetaDisparador(d)}
                                    </Tag>
                                ))}
                            </div>

                            <div style={{ marginBottom: 14 }}>
                                <strong style={{ fontSize: 13 }}>Trabajo de background: </strong>
                                {snapshot.trabajos && snapshot.trabajos.length ? (
                                    snapshot.trabajos.map((t, i) => (
                                        <Tag key={i} color="volcano">
                                            {t.nombre} ({formatMs(t.edadMs)})
                                        </Tag>
                                    ))
                                ) : (
                                    <span style={{ color: "#888" }}>
                                        ningún cron ni proceso masivo corriendo — el bloqueo vino de un
                                        request
                                    </span>
                                )}
                            </div>

                            <h4 style={{ marginBottom: 2 }}>Qué estaba corriendo en ese instante</h4>
                            <p style={{ marginBottom: 8, fontSize: 12, color: "#888" }}>
                                Ordenado del que lleva más tiempo al que menos. El primero es el que más
                                rato llevaba ocupando el proceso.
                            </p>
                            <TableItem
                                dataSource={requestsDetalle}
                                columns={columnas}
                                size="small"
                                rowKey={(r, i) => `${r.method}-${r.originalUrl}-${i}`}
                                defaultPageSize={100}
                                rowClassName={(r, i) =>
                                    i === 0 ? "alta-importancia" : i % 2 === 0 ? "zebra-gris" : "default"
                                }
                            />
                        </>
                    )}
                </Spin>
            </Drawer>
        );
    }

    render() {
        return (
            <>
                {this.renderControles()}
                <Spin spinning={this.state.loadingResumen}>{this.renderKpis()}</Spin>
                <Spin spinning={this.state.loadingSerie}>
                    {this.renderPresion()}
                    {this.renderCpuYPool()}
                    {this.renderMemoria()}
                </Spin>
                {this.renderSaturaciones()}
                <Spin spinning={this.state.loadingTareas}>{this.renderTareas()}</Spin>
                {this.renderDrawer()}
            </>
        );
    }
}

export default Recursos;
